import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import * as query from "@arcgis/core/rest/query";
import Query from "@arcgis/core/rest/support/Query";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import FieldNames from '../defaults/FieldNames.json';
import FieldInfo from '../components/details/FieldInfo.json'
import {v4 as uuidv4} from 'uuid';
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import * as projection from "@arcgis/core/geometry/projection";
import Request from './request';
import {getLower48States} from "../components/discovery/filter/customFilters/FilterHelper";
import Point from "@arcgis/core/geometry/Point";
import GeneralService from "./GeneralService";
import ErrorService from "./ErrorService";

/*
Derivable attributes:
    IsWUI -> Manual for now
    Done -> Latitude, Longitude, Region, TribeName, CongressionalDistrictNumber, County, State, AcresCalculation, Unit, Agency, Department
*/
const DerivationService = {

  deriveAttributesForGeometry: function (geo, isNew, isTreatment, geometryType, isActuals, config, userRoleInfo, isVegetationManual, isPostFire, isPoint, setShowGlobalLoader) {
    try
    {
      const outerThis = this;
      const lower48States = getLower48States();

      return new Promise((resolve) => {
        resolve(projection.load().then(async function () {
          const nad83Geometry = projection.project(geo, new SpatialReference({ wkid: 4269 }));
          let attributes = {};
          let isPostFirePoint = (geometryType === FieldNames.joinFeatureValues.point && isPostFire) ? true : false;
          let isLine = geometryType === FieldNames.joinFeatureValues.line ? true : false;
          const latLong = outerThis.getLatLong(nad83Geometry, isPostFirePoint, isLine);
          let fields = FieldNames.featureFields;

          if(isPostFire && !attributes[fields.entityType]){
            attributes[fields.entityType] = FieldNames.internalEntityTypes.postFire;
          } else {
            attributes[fields.entityType] = FieldNames.internalEntityTypes.fuels;
          }

          // TODO: break up large chunks of contiguous code into separate methods if simple to do
          if (!isTreatment) {
            attributes[fields.latitude] = latLong?.latitude;
            attributes[fields.longitude] = latLong?.longitude;
            // capture activities that are points (buffered to polygons) or post fire points as 0 acres
            attributes[fields.calculatedArea] = (isPostFirePoint || isPoint) ? 0 : await outerThis.getCalculatedArea(nad83Geometry, isLine);
            outerThis.getAreaUnits(attributes, fields, isLine, isPoint)
            attributes[fields.isDirty] = true;

            if (isNew) {
              if(!attributes[fields.isPoint]){
                if (isPoint) {
                  attributes[fields.isPoint] = 'Yes';
                } else {
                  attributes[fields.isPoint] = 'No';
                }
              }
              attributes[fields.isNew] = true;
              let id = uuidv4();
              let entType;
              if (isActuals) {
                entType = isPostFire ? FieldNames.internalEntityTypes.postFire : FieldNames.internalEntityTypes.fuels;
              } else {
                attributes[fields.status] = null;
                entType = isPostFire ? FieldNames.internalEntityTypes.postFire : FieldNames.internalEntityTypes.fuels;
              }
              attributes[fields.entityID] = id;
              attributes[fields.entityType] = entType
            }
          } else {
            attributes[fields.latitude] = latLong?.latitude;
            attributes[fields.longitude] = latLong?.longitude;
            attributes[fields.calculatedArea] = (isPostFirePoint || isPoint) ? 0 : await outerThis.getCalculatedArea(nad83Geometry, isLine);
            outerThis.getAreaUnits(attributes, fields, isLine, isPoint)
            attributes[fields.isDirty] = true;

            if (isNew) {
              if(!attributes[fields.isPoint]){
                if (isPoint) {
                  attributes[fields.isPoint] = 'Yes';
                } else {
                  attributes[fields.isPoint] = 'No';
                }
              }
              attributes[fields.isNew] = true;

              let id = uuidv4();
              let entType;
              if (isActuals) {
                entType = isPostFire ? FieldNames.internalEntityTypes.postFire : FieldNames.internalEntityTypes.fuels;
              } else {
                attributes[fields.status] = null;
                entType = isPostFire ? FieldNames.internalEntityTypes.postFire : FieldNames.internalEntityTypes.fuels;
              }
              attributes[fields.entityID] = id;
              attributes[fields.entityType] = entType
            }
          }

          //Had to stack the promises due to needing the agency before a possible call to derive the unit for bia
          return new Promise((resolve) => {
            Promise.allSettled([
              outerThis.deriveTribe(nad83Geometry, config, isPostFirePoint, isLine),
              outerThis.deriveLocal(nad83Geometry, config, isPostFirePoint, outerThis, isLine),
              outerThis.deriveState(nad83Geometry, config, isPostFirePoint, isLine)
            ]).then(async results => {
              const tribeData = (results && results[0] && results[0].status === "fulfilled") ? results[0].value : null;
              const localData = (results && results[1] && results[1].status === "fulfilled") ? results[1].value : null;
              const stateData = (results && results[2] && results[2].status === "fulfilled") ? results[2].value : null;
              const agency = (localData && localData.agencyName) ? localData.agencyName : "";
              let tribeName = "";
              if(stateData && lower48States && lower48States.includes(stateData.stateAbbreviation)){
                tribeName = (tribeData) ? tribeData : "";
              }

              // TODO: maybe pull promise.allsettled blocks out? Just high level blocks
              return new Promise( (innerResults) => {
                Promise.allSettled([
                  outerThis.deriveCongressionalDistrict(nad83Geometry, isPostFirePoint, config, isLine),
                  outerThis.deriveCounty(nad83Geometry, isPostFirePoint, config, isLine),
                  (agency.includes(FieldNames.agencies.bia) && tribeName) ? outerThis.deriveBIAUnitBasedOnTribe(tribeName, config) : 
                  ((agency.includes(FieldNames.agencies.blm)) ? outerThis.deriveBLMUnit(outerThis, config, nad83Geometry, isPostFirePoint) : 
                  null)
                ]).then(innerResults => {
                  const congressionalDistrict = (innerResults && innerResults[0] && innerResults[0].status === "fulfilled") ? innerResults[0].value : "N/A";
                  attributes[fields.county] = (innerResults && innerResults[1] && innerResults[1].status === "fulfilled") ? innerResults[1].value : "N/A";
                  attributes[fields.congressionalDistrictNumber] = (stateData && stateData.stateAbbreviation && congressionalDistrict) ? stateData.stateAbbreviation + "-" + congressionalDistrict : congressionalDistrict;
                  if(stateData && lower48States && lower48States.includes(stateData.stateAbbreviation)){
                    attributes[fields.tribeName] = (tribeName) ? tribeName : "N/A";
                  } else {
                    attributes[fields.tribeName] = "N/A";
                  }
                  if(agency && (agency.toLowerCase().includes(FieldNames.agencies.bia.toLowerCase()) || agency.toLowerCase().includes(FieldNames.agencies.blm.toLowerCase()))){
                    attributes[fields.unit] = (innerResults && innerResults[2] && innerResults[2].status === "fulfilled" && innerResults[2].value) ? innerResults[2].value : "N/A";
                  } else {
                    attributes[fields.unit] = (localData && localData.unitName) ? localData.unitName : "N/A";
                  }

                  if (localData && localData.departmentName) {
                    attributes[fields.department] = localData.departmentName;
                    attributes[fields.isDepartmentManual] = FieldNames.yesNo.no;
                  } else {
                    attributes[fields.department] = "N/A";
                    attributes[fields.isDepartmentManual] = FieldNames.yesNo.yes;
                  }
                  attributes[fields.agency] = (agency) ? agency : "N/A";
                  attributes[fields.region] = (localData && localData.regionName) ? localData.regionName : "N/A";

                  outerThis.overrideDerivationNA(attributes, fields, FieldNames, userRoleInfo);
                  
                  if (stateData) {
                    attributes[fields.state] = stateData.stateName;
                    if (stateData.stateName === null || stateData.stateName === undefined || stateData.stateName === "Alaska") 
                    { 
                      if(stateData.stateName === "Alaska"){
                        resolve({attributes: attributes, error: "Vegetation Departure Data for Alaska is unavailable"});
                      } else {
                        resolve({attributes: attributes, error: "No State Data Available"});
                      }
                    }
                    else 
                    { //If we have a state name, we can derive the VCC and VDEP
                      if(!isActuals && isTreatment && !isVegetationManual && !isPostFire){
                        //If vegetation departure index has been manually set don't derive any part of it
                        const isConus = (stateData.stateName !== "Hawaii");
                        // outerThis.deriveVegetationConditionClass(nad83Geometry, config, isConus).then((vcc) => {
                        //   attributes[fields.fireRegimeConditionClass] = vcc;

                        return outerThis.deriveVegetationDepartureIndex(nad83Geometry, config, isConus).then((vdep) => {
                          attributes[fields.vegDeparturePercentageDerived] = vdep;
                          if(!isVegetationManual){
                            attributes[fields.vegDeparturePercentageManual] = vdep;
                            attributes[fields.isVegetationManual] = FieldNames.yesNo.no;
                          }
                          resolve({attributes: attributes, error: null});
                        }).catch((error) => {
                          resolve({attributes: attributes, error: error});
                        });
                        //});
                      } else {
                        resolve({attributes: attributes, error: null});
                      }
                    }
                  }
                  else 
                  {
                    resolve({attributes: attributes, error: "No State Data Available"});
                  }
                });
              });
            });
          });
        }));
      });
    }
    catch(e)
    {
      //Turning off in case we error out of Derivations
      if(setShowGlobalLoader){
        setShowGlobalLoader(false);
      }
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getLatLong: function (geometry, isPostFirePoint, isLine) {
    //Currently the centroid is used for everything -> When this is switched be sure to still use the centroid for points
    let centroid = {
      latitude: null,
      longitude: null
    };
    centroid = this.getCentroid(geometry, isPostFirePoint, isLine);
    return{
      latitude: centroid ? parseFloat(centroid.latitude).toFixed(6) : null,
      longitude: centroid ? parseFloat(centroid.longitude).toFixed(6) : null
    }
  },

  getCalculatedArea: function (geometry, isLine = false) {
    try
    {
      return projection.load().then(function () {
        const projectedGeom = projection.project(geometry, new SpatialReference({ wkid: 4326 }));
        const size = isLine ? geometryEngine.geodesicLength(projectedGeom, 'feet') : geometryEngine.geodesicArea(projectedGeom, 'acres')
        const floatVal = parseFloat(size.toFixed(1));
         if(floatVal < 1){
          return 1
        } else if (isLine) {
           return floatVal;
         } else {
           return Math.round(floatVal);
         }
      })
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },

  // deriveVegetationConditionClass: function (polygon, config, isConus) {

  //   const serviceConfig = config.vccImageService;
  //   const endPoint = (isConus) ? serviceConfig.conusUrl : serviceConfig.hawaiiUrl;
  //   const wkid = (isConus) ? serviceConfig.conusWkid : serviceConfig.hawaiiWkid;
  //   const projectedGeometry = projection.project(polygon, new SpatialReference({wkid: wkid}));

  //   return new Promise((resolve, reject) => {

  //     new Request().makeRequest({
  //         url: endPoint,
  //         method: 'get',
  //         data: {
  //             f: 'json',
  //             geometryType: 'esriGeometryPolygon',
  //             geometry: JSON.stringify(projectedGeometry)
  //         }
  //     }).then(
  //         resp => {
  //             const meanPixelValue = resp.statistics[0].mean;
  //             const nearestVcc = Math.round(meanPixelValue);
  //             resolve(nearestVcc);
  //         },
  //         error => {
  //           console.log(`Vegetation Condition Class derivation is unavailable: ${error}`);
  //           reject(error)
  //         }
  //     );
  //   });
  // },

  fetchDataWithRetry: async function (url, queryObject, retries = 0, delay = 0, timeout = 0) {
    var queryOptions = {};
    queryOptions["timeout"] = timeout;

    return query.executeQueryJSON(url, queryObject, queryOptions).then ((results) => {
      if(results){
        if (results.features) {
          return results;
        }
        if (results.error) {
          if (retries > 0) {
            // to log results.error?.message
          } else {
            throw new Error(`Request to ${url} failed with error ${results.error}.`);
          }
        }
      } else {
        if (retries > 0) {
          new Promise(resolve => setTimeout(resolve, delay));
          return this.fetchDataWithRetry(url, queryObject, retries - 1, delay, timeout);
        } else {
          throw new Error(`All retries failed to produce a usable result with features key from ${url}.`);
        }
      }
    }).catch((error) => {
      if (retries > 0) {
          new Promise(resolve => setTimeout(resolve, delay));
          return this.fetchDataWithRetry(url, queryObject, retries - 1, delay, timeout);
        } else {
          throw new Error(`All retries failed for ${url} with error ${error}.`);
        }  
    });
  },

  deriveVegetationDepartureIndex : async function (geo, config, isConus) {
    try
    {
      const serviceConfig = config.vdepImageService;
      const endPoint = (isConus) ? serviceConfig.conusUrl : serviceConfig.hawaiiUrl;
      const wkid = (isConus) ? serviceConfig.conusWkid : serviceConfig.hawaiiWkid;
      const projectedGeometry = projection.project(geo, new SpatialReference({wkid: wkid}));

      return new Promise((resolve, reject) => {
        new Request().makeRequest({
            url: endPoint,
            method: 'get',
            data: {
                f: 'json',
                geometryType: 'esriGeometryPolygon',
                geometry: JSON.stringify(projectedGeometry)
            }
        }).then(
            resp => {
              if(resp && resp.statistics && resp.statistics.length > 0){
                const meanPixelValue = resp.statistics[0].mean;
                const nearestVDEP = Math.round(meanPixelValue);
                resolve(nearestVDEP);
              } else {
                resolve(null);
              }
            },
            error => {
              reject(`Vegetation Departure Percentage Derivation is unavailable: ${error.code}`)
            }
        );
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getCentroid: function(geometry, isPostFirePoint, isLine){
    let centroid = {
      latitude: null,
      longitude: null
    };
    if(isPostFirePoint) {
      centroid = new Point(geometry);
    } else if(isLine) {
      //Passing the entire line will result in multiple ownership (if the line crosses ownership boundaries)
      const line = new Polyline(geometry);
      const nearestVer = geometryEngine.nearestVertex(line, line.extent.center);
      centroid = new Point(nearestVer.coordinate);
    } else {
      centroid = new Polygon(geometry).centroid;
    }
    return centroid
  },
  deriveCongressionalDistrict: async function (geo, isPostFirePoint, config, isLine) {
    try {
      const serviceConfig = config.congressionalDistrict;
      const mercatorGeometry = projection.project(geo, new SpatialReference({wkid: 102100 })); 
      const centroid = this.getCentroid(mercatorGeometry, isPostFirePoint, isLine);
      //Currently the centroid is used for everything -> When this is switched be sure to still use the centroid for points
      const queryObject = new Query({
        geometry: centroid,
        returnGeometry: false,
        outFields: serviceConfig.derivationOutField
      });
  
      return this.fetchDataWithRetry(serviceConfig.url, queryObject, 3, 2000, 5000).then(results => {
        if(results && results.features && results.features.length > 0){
          return results.features[0].attributes[serviceConfig.derivationOutField];         
        } else {
          return null;       
        }
      }).catch(error => {
        console.error("Error:", error);
        return new Promise((resolve,reject) => {
          reject(error);
        });
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }

  },

  // deriveBIARegion: async function (geo, config, isPostFirePoint) {
  //   try {
  //     const serviceConfig = config.BIARegion;

  //     //Currently the centroid is used for everything -> When this is switched be sure to still use the centroid for points
  //     const queryObject = new Query({
  //       geometry: isPostFirePoint ? geo : geo.centroid,
  //       returnGeometry: false,
  //       outFields: serviceConfig.derivationOutField
  //     });

  //     return this.fetchDataWithRetry(serviceConfig.url, queryObject, 3, 2000, 5000).then(results => {
  //       if(results && results.features && results.features.length > 0){
  //         return results.features[0].attributes[serviceConfig.derivationOutField];         
  //       } else {
  //         return null;       
  //       }
  //     }).catch(error => {
  //       console.error("Error:", error);
  //       return new Promise((resolve,reject) => {
  //         reject(error);
  //       });
  //     });
  //   }
  //   catch(e)
  //   {
  //     return new Promise((resolve,reject) => {
  //       reject(e);
  //     });
  //   }
  // },

  deriveRegion: function (geo, config, agency, isPostFirePoint, isLine) {
    try
    {
      let currentAgency = 'Other';
      if(agency){
        currentAgency = agency.includes(FieldNames.agencies.nps) ? FieldNames.agencies.nps : agency.includes(FieldNames.agencies.fws) ? FieldNames.agencies.fws : agency.includes(FieldNames.agencies.bia) ? FieldNames.agencies.bia : 
          agency.includes(FieldNames.agencies.owf) ? FieldNames.agencies.owf : agency.includes(FieldNames.agencies.blm) ? FieldNames.agencies.blm : agency.includes(FieldNames.agencies.bor) ? FieldNames.agencies.bor : 'Other';
      }
      let serviceConfig;
      switch (currentAgency) {
        case FieldNames.agencies.nps:
          serviceConfig = config.NPSLegacyRegions;
          break;
        case FieldNames.agencies.fws:
          serviceConfig = config.FWSRegion;
          break;
        case FieldNames.agencies.bia:
          serviceConfig = config.BIARegion;
          break;
        case FieldNames.agencies.bor:
          serviceConfig = config.BORRegion;
          break;
        case FieldNames.agencies.blm:
          serviceConfig = config.BLMRegion;
          break;
        case FieldNames.agencies.owf:
          serviceConfig = config.DOIUnifiedRegions;
          break;
        default:
          serviceConfig = null;
          break;
      }

      //Currently the centroid is used for everything -> When this is switched be sure to still use the centroid for points
      if(serviceConfig){
        const centroid = this.getCentroid(geo, isPostFirePoint, isLine);
        const queryObject = new Query({
          geometry: centroid,
          returnGeometry: false,
          outFields: serviceConfig.derivationOutField
        });

        return this.fetchDataWithRetry(serviceConfig.url, queryObject, 3, 2000, 5000).then(results => {
          if(results && results.features && results.features.length > 0){
            return results.features[0].attributes[serviceConfig.derivationOutField];         
          } else {
            return null;       
          }
        }).catch(error => {
          console.error("Error:", error);
          return new Promise((resolve,reject) => {
            reject(error);
          });
        });
      }
      else {
        return new Promise((resolve,reject) => {
          resolve(null);
        });
      }
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },

  deriveTribe: async function (geo, config, isPostFirePoint, isLine) {
    try {
      const serviceConfig = config.tribe;
      const mercatorGeometry = projection.project(geo, new SpatialReference({wkid: 102100 })); 
      const centroid = this.getCentroid(mercatorGeometry, isPostFirePoint, isLine);
  
      //Currently the centroid is used for everything -> When this is switched be sure to still use the centroid for points
      const queryObject = new Query({
        geometry: centroid,
        returnGeometry: false,
        outFields: serviceConfig.derivationOutField
      });
  
      return this.fetchDataWithRetry(serviceConfig.url, queryObject, 3, 2000, 5000).then(results => {
        if(results && results.features && results.features.length > 0){
          return results.features[0].attributes[serviceConfig.derivationOutField];         
        } else {
          return null;       
        }
      }).catch(error => {
        console.error("Error:", error);
        return new Promise((resolve,reject) => {
          reject(error);
        });
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },

  deriveBIAUnitBasedOnTribe: async function (tribeName, config) {
    try
    {
      const serviceConfig = config.tribeLeaders;

      const queryObject = new Query({
        where: `(${serviceConfig.derivationCheckField} = '${tribeName}')`,
        returnGeometry: false,
        outFields: serviceConfig.derivationOutField
      });
      const sanitizedQueryObj = GeneralService.sanitizeQueryObject(queryObject);
      return this.fetchDataWithRetry(serviceConfig.url, sanitizedQueryObj, 3, 2000, 5000).then(results => {
        if(results && results.features && results.features.length > 0){
          return results.features[0].attributes[serviceConfig.derivationOutField];
        } else {
          return null;       
       }
      }, function(error){
        console.log(`Tribe Service is unavailable: ${error}`);
        return new Promise((resolve,reject) => {
          resolve(null);
        });
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },

  deriveBLMUnit: async function (outerThis, config, nad83Geometry, isPostFirePoint) {
    try
    {
      return new Promise((resolve) => {
        outerThis.getBlmOtherUnits(config, nad83Geometry, isPostFirePoint).then((oresp) => {
          outerThis.getBlmFieldUnits(config, nad83Geometry, isPostFirePoint).then((fresp) => {
            let otherUnits = (oresp && oresp.length > 0) ? oresp.map(x => x.ADMU_NAME) : [];
            if(otherUnits.length === 1)
            {
              resolve(otherUnits[0]);
            }
  
            let fieldUnits = (fresp && fresp.length > 0) ? fresp.map(x => x.ADMU_NAME) : [];
            if(otherUnits.length === 0 && fieldUnits.length === 1)
            {
              resolve(fieldUnits[0]);
            }
  
            let finalUnits = [...otherUnits, ...fieldUnits];
            let unique = [...new Set(finalUnits)];
            if(unique.length > 0)
            {
              resolve(unique[0]);
            }
            else
            {
              resolve(null);
            }
          })
        })
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },

  getBlmFieldUnits: function(config, geo, isPostFirePoint) {
    try
    {
      const serviceConfig = config.BLMField;
      const mercatorGeometry = projection.project(geo, new SpatialReference({wkid: 102100 })); 

      const queryObject = new Query({
        where: `(OBJECTID IS NOT NULL)`,
        geometry: mercatorGeometry.centroid,
        outFields: "ADMU_NAME",
        returnDistinctValues: true,
        orderByFields: "ADMU_NAME",
        returnGeometry: false,
      });
      return this.fetchDataWithRetry(serviceConfig.url, queryObject, 3, 2000, 5000).then(results => {
        let items = [];
        if(results && results.features && results.features.length > 0) {
          items = results.features.map((eaItem) => eaItem.attributes);
        } 
        return items;
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },

  getBlmOtherUnits: function(config, geo, isPostFirePoint) {
    try
    {
      const serviceConfig = config.BLMField;
      const mercatorGeometry = projection.project(geo, new SpatialReference({wkid: 102100 })); 

      const queryObject = new Query({
        where: `(OBJECTID IS NOT NULL)`,
        geometry: mercatorGeometry.centroid,
        outFields: "ADMU_NAME",
        returnDistinctValues: true,
        orderByFields: "ADMU_NAME",
        returnGeometry: false,
      });
      return this.fetchDataWithRetry(serviceConfig.url, queryObject, 3, 2000, 5000).then(results => {
        let items = [];
        if(results && results.features && results.features.length > 0) {
          items = results.features.map((eaItem) => eaItem.attributes);
        } 
        return items;
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },

  deriveLocal: async function (geo, config, isPostFirePoint, outerThis, isLine) {
    try
    {
      const serviceConfig = config.jurisdictional;
      const mercatorGeometry = projection.project(geo, new SpatialReference({wkid: 102100 })); 
      const centroid = this.getCentroid(mercatorGeometry, isPostFirePoint, isLine);
    
      //Currently the centroid is used for everything -> When this is switched be sure to still use the centroid for points
      const queryObject = new Query({
        geometry: centroid,
        returnGeometry: false,
        outFields: serviceConfig.derivationOutField
      });

      return this.fetchDataWithRetry(serviceConfig.url, queryObject, 3, 2000, 5000).then(results => {
        let unit = "";
        let department = "";
        let agency = "";
        let unitId = "";
        if(results && results.features){
          results.features.forEach((eaFeature) => {
            if(eaFeature && eaFeature.attributes){
              if (eaFeature.attributes[serviceConfig.unitName]) {
                unit = eaFeature.attributes[serviceConfig.unitName];
              }
              if(eaFeature.attributes[serviceConfig.unitID]){
                unitId = eaFeature.attributes[serviceConfig.unitID];
              }
              if (eaFeature.attributes[serviceConfig.departmentName]) {
                if(eaFeature.attributes[serviceConfig.departmentName] 
                  && (eaFeature.attributes[serviceConfig.departmentName].toLowerCase().includes("interior") || eaFeature.attributes[serviceConfig.departmentName].toLowerCase().includes("doi")))
                {
                  department = FieldNames.defaults.department;
                }
                else
                {
                  department = eaFeature.attributes[serviceConfig.departmentName];
                }
              }
              if (eaFeature.attributes[serviceConfig.agencyName]) {
                if(eaFeature.attributes[serviceConfig.agencyName].toLowerCase() === "usfws")
                {
                  agency = "FWS";
                } else
                {
                  agency = eaFeature.attributes[serviceConfig.agencyName];
                }
              }
            }
          });
          if(agency){
            return outerThis.deriveRegion(geo, config, agency, isPostFirePoint, isLine).then((region) => {
              return ({
                unitName: unit,
                departmentName: department,
                agencyName: agency,
                unitID: unitId, 
                regionName: region
              })
            })
          } else {
            return ({
              unitName: unit,
              departmentName: department,
              agencyName: agency,
              unitID: unitId, 
              regionName: null
            })
          }
        } else {
          return ({
            unitName: null,
            departmentName: null,
            agencyName: null,
            unitID: null,
            regionName: null
          })
        }
      }, function(error){
        console.log(`Local Service is unavailable: ${error}`);
        return (null);
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },

  deriveCounty: async function (geo, isPostFirePoint, config, isLine) {
    try
    {
      const serviceConfig = config.county;
      const mercatorGeometry = projection.project(geo, new SpatialReference({wkid: 102100 })); 
      const centroid = this.getCentroid(mercatorGeometry, isPostFirePoint, isLine);
    
      //Currently the centroid is used for everything -> When this is switched be sure to still use the centroid for points
      const queryObject = new Query({
        geometry: centroid,
        returnGeometry: false,
        outFields: serviceConfig.derivationOutField
      });

      return this.fetchDataWithRetry(serviceConfig.url, queryObject, 3, 2000, 5000).then(results => {
        if(results && results.features && results.features.length > 0){
          let countyName = results.features[0].attributes[serviceConfig.derivationOutField];
          countyName = countyName.replace("County", "").trim();
          return countyName;
        } else {
          return null;       
        }
      }, function(error){
        console.log(`County Service is unavailable: ${error}`);
        return new Promise((resolve,reject) => {
          resolve(null);
        });
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },

  deriveState: async function (geo, config, isPostFirePoint, isLine) {
    try
    {
      const serviceConfig = config.state;
      const mercatorGeometry = projection.project(geo, new SpatialReference({wkid: 102100 })); 
      const centroid = this.getCentroid(mercatorGeometry, isPostFirePoint, isLine);

      //Currently the centroid is used for everything -> When this is switched be sure to still use the centroid for points
      const queryObject = new Query({
        geometry: centroid,
        returnGeometry: false,
        outFields: serviceConfig.derivationOutField
      });

      return this.fetchDataWithRetry(serviceConfig.url, queryObject, 3, 2000, 5000).then(results => {
        if(results && results.features && results.features.length > 0){
          return ({
            stateName: results.features[0].attributes[serviceConfig.stateNameField],
            stateAbbreviation: results.features[0].attributes[serviceConfig.stateAbbreviationField]
          })
        } else {
          return (null)
        }
      }, function(error){
        console.log(`State Service is unavailable: ${error}`);
        return (null);
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },

  overrideDerivationNA: function (featureAttributes, fields, fieldNames, userInfo)
  {
    try
    {
      if(!featureAttributes)
      {
        return null;
      }

      // Override values, lock fields, and set flag to allow for N/A based on FUELS-528
      const depVal = featureAttributes[fields.department];
      const agencyVal = featureAttributes[fields.agency];
      const regionVal = featureAttributes[fields.region];
      const unitVal = featureAttributes[fields.unit];
      let ancsaPrivateDerivedVals = fieldNames.ancsaPrivateDerivationOverride.derivationValues;
      let regionOverrideVals = fieldNames.regionDerivationOverride.derivationValues;
  
      if(
        (agencyVal && ancsaPrivateDerivedVals.includes(agencyVal)) // FUELS-528 AC1
        )
      {
        let departmentOverride = fieldNames.ancsaPrivateDerivationOverride.derivationOverrides[fields.department];
        let departmentNaValid = fieldNames.ancsaPrivateDerivationOverride.derivationNaValid[fields.department];
        let departmentLock = fieldNames.ancsaPrivateDerivationOverride.derivationLocks[fields.department];
        let agencyOverride = fieldNames.ancsaPrivateDerivationOverride.derivationOverrides[fields.agency];
        let agencyNaValid = fieldNames.ancsaPrivateDerivationOverride.derivationNaValid[fields.agency];
        let agencyLock = fieldNames.ancsaPrivateDerivationOverride.derivationLocks[fields.agency];
        let regionOverride = fieldNames.ancsaPrivateDerivationOverride.derivationOverrides[fields.region];
        let regionNaValid = fieldNames.ancsaPrivateDerivationOverride.derivationNaValid[fields.region];
        let regionLock = fieldNames.ancsaPrivateDerivationOverride.derivationLocks[fields.region];
        let unitOverride = fieldNames.ancsaPrivateDerivationOverride.derivationOverrides[fields.unit];
        let unitNaValid = fieldNames.ancsaPrivateDerivationOverride.derivationNaValid[fields.unit];
        let unitLock = fieldNames.ancsaPrivateDerivationOverride.derivationLocks[fields.unit];
  
        featureAttributes[fields.department] = departmentOverride ? departmentOverride : featureAttributes[fields.department];
        featureAttributes[fields.agency] = agencyOverride ? agencyOverride : featureAttributes[fields.agency];
        featureAttributes[fields.region] = regionOverride ? regionOverride : featureAttributes[fields.region];
        featureAttributes[fields.unit] = unitOverride ? unitOverride : featureAttributes[fields.unit];
        
        if(departmentNaValid || departmentLock || agencyNaValid || agencyLock ||
          regionNaValid || regionLock || unitNaValid || unitLock)
          {
            featureAttributes[fields.locationDepartmentNaValid] = departmentNaValid ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationDepartmentLocked] = departmentLock ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationAgencyNaValid] = agencyNaValid ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationAgencyLocked] = agencyLock ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationRegionNaValid] = regionNaValid ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationRegionLocked] = regionLock ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationUnitNaValid] = unitNaValid ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationUnitLocked] = unitLock ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
          }
      }
      else if(
        (agencyVal && regionOverrideVals.includes(agencyVal)) // FUELS-528 AC2/3
        )
      {
        let departmentOverride = fieldNames.regionDerivationOverride.derivationOverrides[fields.department];
        let departmentNaValid = fieldNames.regionDerivationOverride.derivationNaValid[fields.department];
        let departmentLock = fieldNames.regionDerivationOverride.derivationLocks[fields.department];
        let agencyOverride = fieldNames.regionDerivationOverride.derivationOverrides[fields.agency];
        let agencyNaValid = fieldNames.regionDerivationOverride.derivationNaValid[fields.agency];
        let agencyLock = fieldNames.regionDerivationOverride.derivationLocks[fields.agency];
        let regionOverride = fieldNames.regionDerivationOverride.derivationOverrides[fields.region];
        let regionNaValid = fieldNames.regionDerivationOverride.derivationNaValid[fields.region];
        let regionLock = fieldNames.regionDerivationOverride.derivationLocks[fields.region];
        let unitOverride = fieldNames.regionDerivationOverride.derivationOverrides[fields.unit];
        let unitNaValid = fieldNames.regionDerivationOverride.derivationNaValid[fields.unit];
        let unitLock = fieldNames.regionDerivationOverride.derivationLocks[fields.unit];
  
        // Set Department and Unit to N/A if blank
        featureAttributes[fields.department] = departmentOverride ? departmentOverride : (featureAttributes[fields.department] ? featureAttributes[fields.department] : "N/A");
        featureAttributes[fields.agency] = agencyOverride ? agencyOverride : featureAttributes[fields.agency];
        featureAttributes[fields.region] = regionOverride ? regionOverride : featureAttributes[fields.region];
        featureAttributes[fields.unit] = unitOverride ? unitOverride : (featureAttributes[fields.unit] ? featureAttributes[fields.unit] : "N/A");
        
        if(departmentNaValid || departmentLock || agencyNaValid || agencyLock ||
          regionNaValid || regionLock || unitNaValid || unitLock)
          {
            featureAttributes[fields.locationDepartmentNaValid] = departmentNaValid ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationDepartmentLocked] = departmentLock ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationAgencyNaValid] = agencyNaValid ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationAgencyLocked] = agencyLock ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationRegionNaValid] = regionNaValid ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationRegionLocked] = regionLock ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationUnitNaValid] = unitNaValid ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
            featureAttributes[fields.locationUnitLocked] = unitLock ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
          }
      }
      else // FUELS-528 AC4
      {
        // Set variables to false even if they already were in case of rederive changing values
        featureAttributes[fields.locationDepartmentNaValid] = FieldNames.yesNo.no;
        featureAttributes[fields.locationDepartmentLocked] = FieldNames.yesNo.no;
        featureAttributes[fields.locationAgencyNaValid] = FieldNames.yesNo.no;
        featureAttributes[fields.locationAgencyLocked] = FieldNames.yesNo.no;
        featureAttributes[fields.locationRegionNaValid] = FieldNames.yesNo.no;
        featureAttributes[fields.locationRegionLocked] = FieldNames.yesNo.no;
        featureAttributes[fields.locationUnitNaValid] = FieldNames.yesNo.no;
        featureAttributes[fields.locationUnitLocked] = FieldNames.yesNo.no;
      }
    }
    catch(e)
    {
      ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], e);
      return null;
    }
  },

  getAreaUnits: function(attributes, fields, isLine, isPoint) {
    if (isLine){
      if (attributes[fields.calculatedArea] > 5280) {
        attributes[fields.calculatedArea] = parseFloat((attributes[fields.calculatedArea] / 5280).toFixed(1))
        attributes[fields.areaUnit] = FieldInfo.AreaUnit.domain.codedValues.find(x => x.code === 'Miles').code;
        } else {
          attributes[fields.areaUnit] = FieldInfo.AreaUnit.domain.codedValues.find(x => x.code === 'Feet').code;
      }
    } else if (!isPoint){
      attributes[fields.areaUnit] = FieldInfo.AreaUnit.domain.codedValues.find(x => x.code === 'Acres').code;
    } else {
      attributes[fields.areaUnit] = "";
    }
  }
};

export default DerivationService;